import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  ViewChildren
} from '@angular/core';
import { TopLevelPatientData } from '../../models/TopLevelPatientData';
import { from, Observable, Subscription } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ScriptInjectorService } from 'src/app/core/services/script-injector.service';
import { select, Store } from '@ngrx/store';
import * as fromRoot from '../../../reducers';
import * as fromAuth from '../../../auth/reducers';
import { map } from 'rxjs/operators';
import { ShowNewConversationToastV3 } from 'src/app/messagingV3/legacy/actions/new-message.actions';

declare global {
  interface HTMLElementEventMap {
    'conversation-created': Event & {
      payload: {
        conversationId: number;
        folderId: number;
        patients: {
          id: number;
          firstName: string;
          lastName: string;
        }[];
      };
    };
  }
}

@Component({
  selector: 'portal-patient-top-level',
  templateUrl: './patient-top-level.component.html',
  styleUrls: ['./patient-top-level.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PatientTopLevelComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  @ViewChildren('createConversationComponent')
  createConversationComponents: QueryList<ElementRef<HTMLElement>>;

  public useV3Messaging$: Observable<boolean>;
  public clinicMessagingLoaded$: Observable<boolean>;
  public selectedLanguage$: Observable<string | null>;
  public xSalveClinicToken$: Observable<string | null>;
  @Input() text: any;
  @Input() patient: TopLevelPatientData;
  @Input() videoCallsEnabled: boolean;
  @Output() openNewMessage = new EventEmitter();
  @Output() openNewVideoCall = new EventEmitter();

  private _subscriptions = new Subscription();

  constructor(
    private _store: Store<fromRoot.State>,
    private _scriptInjector: ScriptInjectorService
  ) {}

  ngOnInit(): void {
    this.selectedLanguage$ = this._store.pipe(select(fromAuth.getLanguageCode));
    this.xSalveClinicToken$ = this._store.pipe(select(fromAuth.getClinicId));
    this.useV3Messaging$ = this._store.pipe(
      select(fromAuth.getMessagingV3Enabled),
      map((enabled) => (enabled !== undefined ? enabled : false))
    );
    this.clinicMessagingLoaded$ = from(
      this._scriptInjector
        .loadScript('salve-clinic-messaging', environment.messagingComponentUrl)
        .then(() => true)
        .catch(() => false)
    );
  }

  ngAfterViewInit(): void {
    const handler = this.onConversationCreated.bind(this);
    this._subscriptions.add(
      this.createConversationComponents.changes.subscribe(
        () => {
          const component = this.createConversationComponents.first;
          component.nativeElement.addEventListener(
            'conversation-created',
            handler
          );
        },
        (_error) => {},
        () => {
          this.createConversationComponents.forEach((component) => {
            component.nativeElement.removeEventListener(
              'conversation-created',
              handler
            );
          });
        }
      )
    );
  }

  onConversationCreated(event: HTMLElementEventMap['conversation-created']) {
    if (event.payload && event.payload.conversationId) {
      this._store.dispatch(
        new ShowNewConversationToastV3({
          conversationId: event.payload.conversationId,
          folderId: event.payload.folderId,
          patients: event.payload.patients
        })
      );
    }
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }
}
